import React from 'react';
import { Box, Typography } from '@mui/material';
import { CARD_RARITIES } from '../services/cardService';
import './CardDisplay.css';

function CardDisplay({ card, collected = false, count = 0, size = 'medium', onClick }) {
  const rarityConfig = CARD_RARITIES[card?.rarity] || CARD_RARITIES.COMMON;
  const style = rarityConfig.style;
  const [rotateX, setRotateX] = React.useState(0);
  const [rotateY, setRotateY] = React.useState(0);
  const [glareX, setGlareX] = React.useState(50);
  const [glareY, setGlareY] = React.useState(50);
  const cardRef = React.useRef(null);

  // Generate random sparkle positions for Epic/Legendary cards
  const sparkles = React.useMemo(() => {
    if (card.rarity !== 'EPIC' && card.rarity !== 'LEGENDARY') return [];

    const sparkleCount = card.rarity === 'LEGENDARY' ? 30 : 20;
    return Array.from({ length: sparkleCount }, (_, i) => ({
      id: i,
      x: Math.random() * 100,
      y: Math.random() * 100,
      size: Math.random() * 3 + 1,
      delay: Math.random() * 3,
      duration: Math.random() * 2 + 2,
    }));
  }, [card.rarity]);

  // Size configurations
  const sizes = {
    small: { width: 80, height: 112, fontSize: '0.6rem' },
    medium: { width: 120, height: 168, fontSize: '0.8rem' },
    large: { width: 200, height: 280, fontSize: '1rem' },
  };

  const sizeConfig = sizes[size] || sizes.medium;

  // Mouse/touch move handler for tilt and glare effect
  const handleInteraction = (clientX, clientY) => {
    if (!cardRef.current || !collected) return;

    const rect = cardRef.current.getBoundingClientRect();
    const x = clientX - rect.left;
    const y = clientY - rect.top;
    const centerX = rect.width / 2;
    const centerY = rect.height / 2;

    // Calculate rotation (max 10 degrees for subtlety)
    const rotY = ((x - centerX) / centerX) * 10;
    const rotX = ((centerY - y) / centerY) * 10;

    setRotateX(rotX);
    setRotateY(rotY);

    // Calculate glare position (0-100%)
    const glareXPos = (x / rect.width) * 100;
    const glareYPos = (y / rect.height) * 100;

    setGlareX(glareXPos);
    setGlareY(glareYPos);
  };

  const handleMouseMove = (e) => {
    handleInteraction(e.clientX, e.clientY);
  };

  const handleTouchMove = (e) => {
    if (e.touches.length > 0) {
      handleInteraction(e.touches[0].clientX, e.touches[0].clientY);
    }
  };

  // Reset handler
  const handleInteractionEnd = () => {
    setRotateX(0);
    setRotateY(0);
    setGlareX(50);
    setGlareY(50);
  };

  // If not collected, show placeholder
  if (!collected) {
    return (
      <Box
        className="card-placeholder"
        onClick={onClick}
        sx={{
          width: sizeConfig.width,
          height: sizeConfig.height,
          border: '2px dashed rgba(80, 80, 80, 0.3)',
          borderRadius: '8px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor: 'transparent',
          cursor: onClick ? 'pointer' : 'default',
          transition: 'all 0.3s ease',
          boxSizing: 'border-box',
          '&:hover': onClick ? {
            borderColor: 'rgba(100, 100, 100, 0.5)',
            backgroundColor: 'rgba(80, 80, 80, 0.1)',
          } : {},
        }}
      >
        <Typography
          variant="body2"
          sx={{
            color: '#999',
            fontSize: sizeConfig.fontSize,
            fontWeight: 600,
          }}
        >
          #{card.cardNumber}
        </Typography>
      </Box>
    );
  }

  return (
    <Box
      ref={cardRef}
      className={`card-display ${card.rarity.toLowerCase()}-card`}
      onClick={onClick}
      onMouseMove={handleMouseMove}
      onMouseLeave={handleInteractionEnd}
      onTouchMove={handleTouchMove}
      onTouchEnd={handleInteractionEnd}
      sx={{
        width: sizeConfig.width,
        height: sizeConfig.height,
        borderRadius: '8px',
        position: 'relative',
        cursor: onClick ? 'pointer' : 'default',
        transition: 'transform 0.1s ease-out, box-shadow 0.3s ease',
        boxSizing: 'border-box',
        overflow: 'hidden',
        transformStyle: 'preserve-3d',
        transform: `perspective(1000px) rotateX(${rotateX}deg) rotateY(${rotateY}deg)`,
        boxShadow: card.rarity === 'LEGENDARY'
          ? '0 0 20px rgba(255, 140, 0, 0.6), 0 0 40px rgba(255, 140, 0, 0.3)'
          : '0 4px 8px rgba(0,0,0,0.2)',
        '&:hover': onClick ? {
          boxShadow: card.rarity === 'LEGENDARY'
            ? '0 0 30px rgba(255, 140, 0, 0.8), 0 0 60px rgba(255, 140, 0, 0.4), 0 8px 16px rgba(0,0,0,0.3)'
            : '0 8px 16px rgba(0,0,0,0.3)',
        } : {},
      }}
    >
      {/* Full-size Card Image */}
      {card.imageUrl ? (
        <img
          src={card.imageUrl}
          alt={card.name}
          style={{
            width: '100%',
            height: '100%',
            objectFit: 'cover',
            borderRadius: '8px',
          }}
        />
      ) : (
        <Box
          sx={{
            width: '100%',
            height: '100%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: 'rgba(100, 100, 100, 0.3)',
            borderRadius: '8px',
          }}
        >
          <Typography
            variant="h4"
            sx={{
              color: 'rgba(255, 255, 255, 0.5)',
              fontWeight: 'bold',
              fontSize: sizeConfig.fontSize === '0.6rem' ? '1.5rem' : '3rem',
            }}
          >
            {card.cardNumber}
          </Typography>
        </Box>
      )}

      {/* Rarity Effects Overlay */}
      {/* UNCOMMON: Multiple diagonal shine lines */}
      {card.rarity === 'UNCOMMON' && (
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            background: `
              repeating-linear-gradient(
                ${45 + (glareX - 50) * 0.5}deg,
                transparent,
                transparent 8px,
                rgba(255, 255, 255, 0.03) 8px,
                rgba(255, 255, 255, 0.03) 9px
              ),
              linear-gradient(
                ${135 + (glareX - 50) * 0.3}deg,
                transparent 0%,
                rgba(255, 255, 255, 0) 30%,
                rgba(255, 255, 255, 0.12) 38%,
                rgba(255, 255, 255, 0.2) 40%,
                rgba(255, 255, 255, 0.12) 42%,
                rgba(255, 255, 255, 0) 45%,
                transparent 48%,
                rgba(255, 255, 255, 0) 52%,
                rgba(255, 255, 255, 0.15) 58%,
                rgba(255, 255, 255, 0.25) 60%,
                rgba(255, 255, 255, 0.15) 62%,
                rgba(255, 255, 255, 0) 68%,
                transparent 70%,
                rgba(255, 255, 255, 0) 78%,
                rgba(255, 255, 255, 0.1) 83%,
                rgba(255, 255, 255, 0.18) 85%,
                rgba(255, 255, 255, 0.1) 87%,
                rgba(255, 255, 255, 0) 92%,
                transparent 100%
              )
            `,
            backgroundPosition: `${glareX}% ${glareY}%`,
            backgroundSize: '100% 100%, 250% 250%',
            mixBlendMode: 'overlay',
            opacity: 0.85,
            pointerEvents: 'none',
          }}
        />
      )}

      {/* RARE: Rainbow holographic effect */}
      {card.rarity === 'RARE' && (
        <>
          {/* Base rainbow shimmer */}
          <Box
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              width: '100%',
              height: '100%',
              background: `
                conic-gradient(
                  from ${glareX * 3.6}deg at ${glareX}% ${glareY}%,
                  hsl(0, 100%, 70%) 0deg,
                  hsl(60, 100%, 70%) 60deg,
                  hsl(120, 100%, 70%) 120deg,
                  hsl(180, 100%, 70%) 180deg,
                  hsl(240, 100%, 70%) 240deg,
                  hsl(300, 100%, 70%) 300deg,
                  hsl(0, 100%, 70%) 360deg
                )
              `,
              opacity: 0.3,
              mixBlendMode: 'overlay',
              pointerEvents: 'none',
              filter: 'blur(20px)',
            }}
          />
          {/* Sharp highlight glare */}
          <Box
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              width: '100%',
              height: '100%',
              background: `
                radial-gradient(
                  circle 80px at ${glareX}% ${glareY}%,
                  rgba(255, 255, 255, 0.6) 0%,
                  rgba(255, 255, 255, 0.2) 30%,
                  rgba(255, 255, 255, 0) 60%
                )
              `,
              mixBlendMode: 'overlay',
              opacity: 0.8,
              pointerEvents: 'none',
            }}
          />
          {/* Subtle color shift */}
          <Box
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              width: '100%',
              height: '100%',
              background: `
                linear-gradient(
                  ${(glareX - 50) * 3.6}deg,
                  rgba(255, 100, 200, 0.15),
                  rgba(100, 200, 255, 0.15),
                  rgba(255, 255, 100, 0.15),
                  rgba(100, 255, 200, 0.15)
                )
              `,
              mixBlendMode: 'color-dodge',
              opacity: 0.5,
              pointerEvents: 'none',
            }}
          />
        </>
      )}

      {/* EPIC: Glitter sparkles effect */}
      {card.rarity === 'EPIC' && (
        <>
          {/* Dark overlay for contrast */}
          <Box
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              width: '100%',
              height: '100%',
              background: 'linear-gradient(135deg, rgba(0,0,0,0.1), rgba(0,0,0,0.05))',
              pointerEvents: 'none',
            }}
          />
          {/* Sparkles */}
          {sparkles.map((sparkle) => (
            <Box
              key={sparkle.id}
              sx={{
                position: 'absolute',
                left: `${sparkle.x}%`,
                top: `${sparkle.y}%`,
                width: `${sparkle.size}px`,
                height: `${sparkle.size}px`,
                borderRadius: '50%',
                background: 'radial-gradient(circle, rgba(255,255,255,1) 0%, rgba(200,220,255,0.8) 50%, transparent 100%)',
                boxShadow: '0 0 4px rgba(255,255,255,0.8)',
                animation: `sparkle ${sparkle.duration}s ease-in-out ${sparkle.delay}s infinite`,
                pointerEvents: 'none',
              }}
            />
          ))}
        </>
      )}

      {/* LEGENDARY: Fire underglow + gold glitter sparkles */}
      {card.rarity === 'LEGENDARY' && (
        <>
          {/* Gold/Orange tint overlay */}
          <Box
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              width: '100%',
              height: '100%',
              background: 'linear-gradient(135deg, rgba(255,140,0,0.15), rgba(255,100,0,0.1))',
              mixBlendMode: 'overlay',
              pointerEvents: 'none',
            }}
          />
          {/* Gold sparkles */}
          {sparkles.map((sparkle) => (
            <Box
              key={sparkle.id}
              sx={{
                position: 'absolute',
                left: `${sparkle.x}%`,
                top: `${sparkle.y}%`,
                width: `${sparkle.size}px`,
                height: `${sparkle.size}px`,
                borderRadius: '50%',
                background: 'radial-gradient(circle, rgba(255,215,0,1) 0%, rgba(255,140,0,0.8) 50%, transparent 100%)',
                boxShadow: '0 0 6px rgba(255,215,0,0.9)',
                animation: `sparkle ${sparkle.duration}s ease-in-out ${sparkle.delay}s infinite`,
                pointerEvents: 'none',
              }}
            />
          ))}
        </>
      )}

      {/* Card Info Overlay */}
      <Box
        sx={{
          position: 'absolute',
          bottom: 0,
          left: 0,
          right: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.75)',
          backdropFilter: 'blur(8px)',
          padding: '6px 8px',
          borderBottomLeftRadius: '8px',
          borderBottomRightRadius: '8px',
        }}
      >
        <Typography
          variant="caption"
          sx={{
            color: 'white',
            fontWeight: 600,
            fontSize: sizeConfig.fontSize,
            display: 'block',
            textAlign: 'center',
            textShadow: '0 1px 2px rgba(0,0,0,0.8)',
          }}
        >
          {card.name}
        </Typography>
        <Typography
          variant="caption"
          sx={{
            color: rarityConfig.style.border?.split(' ')[2] || 'rgba(255, 255, 255, 0.8)',
            fontSize: `calc(${sizeConfig.fontSize} * 0.8)`,
            display: 'block',
            textAlign: 'center',
            fontWeight: 500,
          }}
        >
          {rarityConfig.name}
        </Typography>
      </Box>

      {/* Count Badge */}
      {count > 1 && (
        <Box
          sx={{
            position: 'absolute',
            top: 8,
            right: 8,
            backgroundColor: 'rgba(0, 0, 0, 0.85)',
            color: 'white',
            borderRadius: '50%',
            width: size === 'small' ? 24 : 32,
            height: size === 'small' ? 24 : 32,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: size === 'small' ? '0.7rem' : '0.85rem',
            fontWeight: 'bold',
            border: '2px solid white',
            boxShadow: '0 2px 4px rgba(0,0,0,0.5)',
          }}
        >
          {count}
        </Box>
      )}
    </Box>
  );
}

export default CardDisplay;
